/*
 * Widget-defer registry — per-widget config for the widget-defer engine.
 *
 * See:
 *   pdp-load-audit/docs/WIDGET-ORCHESTRATOR-SPEC.md — engine build spec + schema
 *   pdp-load-audit/docs/WIDGET-CAPTURE-METHODOLOGY.md — how the recipes+pixels were captured
 *
 * Schema fields per entry:
 *   id                       — unique key; matches snippet + snapshot filename stems
 *   mode                     — 'placeholder' | 'timing-only'
 *   viewport                 — { min, max } — engine skips entry if window.innerWidth outside range
 *   injection                — how the vendor script gets to the page
 *     .path                  — 'script-src' (MVP); future: 'inline-script' | 'app-embed' | 'fetch-eval' | 'web-pixel'
 *     .sourcePattern         — regex matched against <script src> to catch + defer the vendor loader
 *   placeholder              — visual placeholder (omit when mode === 'timing-only')
 *     .recipeStrategy        — 'serialize-dom' | 'base64-png' | 'hand-crafted-svg' | 'skeleton'
 *     .snippet               — theme snippet path (rendered via {%- render ... -%})
 *     .pixel                 — asset filename (for validation/drift-detect; not runtime-critical)
 *     .selector              — CSS pattern matched against real widget's mounted DOM node (for handoff)
 *   hydration
 *     .triggers              — array; 'natural' | 'post-lcp-idle' | 'scroll-into-view'
 *     .initApi               — string path to function called after vendor scripts load
 *     .clickForward          — replay any pre-hydration clicks after mount
 *   position                 — optional; declarative style overrides applied post-hydration
 *   configReassert           — optional; first-class field for retry-ladder against config-clobbering vendors
 *     .at                    — [ms, ms, ...] — schedule after DCL
 *     .config                — object merged into the vendor's global (e.g. window._support)
 *     .reinvokeInitApi       — call initApi again after each re-assert
 */

window.__widgetDeferRegistry = [
  {
    id: 'reamaze-desktop',
    mode: 'placeholder',
    viewport: { min: 768, max: null },
    injection: {
      path: 'script-src',
      sourcePattern: /reamaze/i,
    },
    placeholder: {
      recipeStrategy: 'serialize-dom',
      snippet: 'widget-placeholder-reamaze-desktop',
      pixel: 'widget-snapshot-reamaze-desktop.png',
      selector: '#reamaze-widget',
    },
    hydration: {
      triggers: ['natural'],
      initApi: 'window.reamaze.initialize',
      /* reuseInPlace: Reamaze vendor reuses the existing #reamaze-widget
         element rather than creating its own bubble. So the engine's
         default hidePlaceholder + replayPendingClick (which poll for a
         :not([data-widget-defer-placeholder]) element) never fire.
         Instead we let the placeholder remain as-is (strip the marker
         attr on hydration) and wire clickHandler for click delivery. */
      reuseInPlace: true,
      clickHandler: function (evt, entry) {
        /* Reamaze's own click listener only attaches on certain viewports
           / session states (verified 2026-08-24: doesn't attach at 768w
           tablet range even with displayOn:'desktop' config winning; also
           unreliable across fresh sessions). Give vendor 300ms to open
           its own popup; if it didn't, call reamaze.popup() directly.

           CRITICAL: capture the pre-click popup state at handler-fire
           time (delegated capture-phase listener runs before vendor's
           listener). If the popup was ALREADY open when the click landed,
           the user is closing it — vendor's listener will handle that;
           don't fire the fallback (it would call popup() again and
           re-open the panel, creating a close+reopen loop on every
           close-click). */
        var popupSel = '#reamaze-popup';
        var isOpen = function () {
          var el = document.querySelector(popupSel);
          return el && el.className && el.className.indexOf('reamaze-visible') !== -1;
        };
        if (isOpen()) return; /* user is closing; vendor handles it */
        setTimeout(function () {
          if (isOpen()) return; /* vendor already opened it */
          if (window.reamaze && typeof window.reamaze.popup === 'function') {
            try { window.reamaze.popup(); } catch (e) {}
          }
        }, 300);
      },
    },
    position: {
      bottom: '60px',
      right: '22px',
    },
    configReassert: {
      at: [0, 500, 1500, 3000],
      config: {
        _support: {
          account: 'aromanticltd',
          ui: {
            widget: {
              displayOn: 'desktop',
              icon: 'chat',
              slug: 'aromantic-ltd-chat-slash-contact-form-shoutbox',
              position: { bottom: '60px', right: '22px' },
            },
          },
        },
      },
      reinvokeInitApi: true,
    },
  },

  {
    id: 'pushowl-desktop',
    mode: 'placeholder',
    viewport: { min: 768, max: null },
    injection: {
      path: 'script-src',
      sourcePattern: /pushowl|pushowl-cdn/i,
    },
    placeholder: {
      recipeStrategy: 'serialize-dom',
      snippet: 'widget-placeholder-pushowl-desktop',
      pixel: 'widget-snapshot-pushowl-desktop.png',
      selector: '.pushowl-simple-toast',
    },
    hydration: {
      triggers: ['post-lcp-idle'],
      initApi: null,
      clickForward: true,
    },
    position: {
      /* Anchor of the bottom-left stack (google-merchant sits above at bottom:98).
         CSS var --pushowl-bottom lifts this on tablet (768-1067) via
         mobile-nav-footer.css. Desktop keeps 15px fallback. On tablet+mobile
         PushOwl is hidden via display:none (see mobile-nav-footer.css) —
         vendor's own tablet listener behaviour is broken and hiding is the
         pragmatic call. */
      bottom: 'var(--pushowl-bottom, 15px)',
      left: '15px',
    },
  },

  {
    id: 'google-merchant',
    mode: 'placeholder',
    /* Desktop-only: on mobile the widget mounts inside the mobile-side-menu
       card via CSS repositioning; we don't want the widget-defer engine's
       position override to fight our per-slot placement. */
    viewport: { min: 768, max: null },
    injection: {
      path: 'script-src',
      /* Actual Google Merchant Customer Reviews loader URL (verified against
         live storefront 2026-08-18). Was previously mis-guessed as
         apis.google.com/js/platform. */
      sourcePattern: /gstatic\.com\/shopping\/merchant|merchantwidget/i,
    },
    placeholder: {
      recipeStrategy: 'base64-png',
      snippet: 'widget-placeholder-google-merchant',
      pixel: 'widget-snapshot-google-merchant.png',
      /* Attribute-starts-with selector matches BOTH placeholder
         (#google-merchantwidget-iframe-wrapper-placeholder) AND vendor's real
         mount (#google-merchantwidget-iframe-wrapper). Vendor script aborts
         if it finds an existing element with its exact id, so we must use a
         unique id for the placeholder — engine still needs to detect the
         real widget via this shared prefix selector. */
      selector: '[id^="google-merchantwidget-iframe-wrapper"]',
    },
    hydration: {
      triggers: ['post-lcp-idle'],
      initApi: null,
      clickForward: false,
    },
    position: {
      /* Vendor wrapper is 84x84 with the visible circle centered inside (14px
         transparent padding on each side). To align visible circle bottom at
         the same viewport y as the placeholder + maintain 15px gap above
         pushowl visible top: bottom = 84 (not 98). Wrapper bottom edge at 84,
         visible circle bottom at 84 + 14 = 98, pushowl top at 83 → 15px gap ✓
         CSS var --merchant-bottom lifts this to 144px on tablet (768-1067) via
         mobile-nav-footer.css. Must lift together with pushowl (144 = 75 + 69)
         to preserve the original 69px relative offset between the two widgets. */
      bottom: 'var(--merchant-bottom, 84px)',
      left: '15px',
    },
  },

  {
    id: 'yotpo-reviews-tab',
    mode: 'placeholder',
    viewport: { min: 768, max: null },
    injection: {
      path: 'script-src',
      sourcePattern: /staticw2\.yotpo\.com|yotpo\.com\/loader/i,
    },
    placeholder: {
      recipeStrategy: 'serialize-dom',
      snippet: 'widget-placeholder-yotpo-reviews-tab',
      pixel: 'widget-snapshot-yotpo-reviews-tab.png',
      selector: '.yotpo-reviews-tab-floating-tab',
    },
    hydration: {
      triggers: ['natural'],
      initApi: null,
      clickForward: true,
    },
  },

  {
    id: 'yotpo-reviews-block',
    mode: 'placeholder',
    viewport: { min: 0, max: null },
    injection: {
      /* Same vendor script as yotpo-reviews-tab; they share the loader.
         The engine dedupes: only the first entry's sourcePattern match triggers
         actual defer, subsequent entries reuse the deferred script. */
      path: 'script-src',
      sourcePattern: /staticw2\.yotpo\.com|yotpo\.com\/loader/i,
    },
    placeholder: {
      recipeStrategy: 'skeleton',
      snippet: 'widget-placeholder-yotpo-reviews-block',
      pixel: 'widget-snapshot-yotpo-reviews-block.png',
      selector: '#yotpo-reviews-main-widget',
    },
    hydration: {
      triggers: ['scroll-into-view'],
      initApi: null,
      clickForward: false,
    },
  },

  {
    id: 'zigpoll',
    mode: 'timing-only',
    viewport: { min: 0, max: null },
    injection: {
      path: 'script-src',
      sourcePattern: /zigpoll\.com/i,
    },
    hydration: {
      triggers: ['post-lcp-idle'],
      initApi: null,
      clickForward: false,
    },
  },
];
